import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePickerComponent, DateRangeMode } from 'components/date-picker/date-picker.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import type { BackofficeInformationRequestCard, ServiceDeadlineType } from 'types/information-request.interface';
import { formatDate } from 'util/date.utility';

const DEADLINE_LABEL_KEYS: Record<Exclude<ServiceDeadlineType, 'Other'>, string> = {
  Days30: 'pages.viewInfoRequest.basicInfo.deadlineDays30',
  Days15: 'pages.viewInfoRequest.basicInfo.deadlineDays15',
  Days10: 'pages.viewInfoRequest.basicInfo.deadlineDays10'
};

export interface InvoiceMarkPaidPayload {
  mustCompleteByDate?: string;
}

@Component({
  selector: 'app-ir-invoice-mark-paid-modal',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, DatePickerComponent, TooltipComponent],
  templateUrl: './mark-paid-modal.component.html'
})
export class IrInvoiceMarkPaidModalComponent implements OnChanges {
  readonly DateRangeMode = DateRangeMode;

  @Input() isOpen = false;
  @Input() informationRequest!: BackofficeInformationRequestCard;
  @Input() isLoading = false;
  @Output() confirmed = new EventEmitter<InvoiceMarkPaidPayload>();
  @Output() closed = new EventEmitter<void>();
  @ViewChild('content') content!: TemplateRef<unknown>;

  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private translateService: TranslateService
  ) {
    this.form = this.fb.group({
      mustCompleteByDate: ['']
    });
  }

  get serviceExecutionDeadlineLabel(): string {
    const deadline = this.informationRequest?.serviceDeadline;
    if (deadline === 'Other') {
      return this.informationRequest?.deadlineDate ? formatDate(this.informationRequest.deadlineDate) : '-';
    }
    const key = deadline ? DEADLINE_LABEL_KEYS[deadline] : null;
    if (!key) return '-';
    const label = this.translateService.instant(key);
    if (deadline === 'Days15' || deadline === 'Days10') {
      return `${label} (${this.translateService.instant('pages.viewInfoRequest.basicInfo.increasedFee')})`;
    }
    return label;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isOpen']) return;
    if (this.isOpen) {
      this.form.reset({ mustCompleteByDate: '' });
      this.modalService.open(this.content, { backdrop: 'static', keyboard: false, windowClass: 'start-processing-modal' });
    } else {
      this.modalService.dismissAll();
    }
  }

  onDateSelected(value: string): void {
    this.form.get('mustCompleteByDate')?.setValue(value);
    this.form.get('mustCompleteByDate')?.markAsTouched();
  }

  confirm(): void {
    if (this.isLoading) return;
    const mustCompleteByDate = this.form.get('mustCompleteByDate')?.value || undefined;
    this.confirmed.emit({ mustCompleteByDate });
  }

  close(): void {
    this.modalService.dismissAll();
    this.closed.emit();
  }
}
